/* eslint-disable */
import { useState, useEffect, useRef } from "react";
import { C, t, fmt$, formatDate } from "../shared/globals.js";

function MonthlyBarsChart({ data }) {
  const wrapRef = useRef(null);
  const [dims, setDims] = useState({ w: 800, h: 260 });
  const [hovered, setHovered] = useState(null);
  const [tooltip, setTooltip] = useState(null);

  useEffect(() => {
    const obs = new ResizeObserver(es => {
      setDims({ w: Math.max(es[0].contentRect.width, 200), h: 260 });
    });
    if (wrapRef.current) {
      obs.observe(wrapRef.current);
      requestAnimationFrame(() => {
        if (wrapRef.current) {
          const { width } = wrapRef.current.getBoundingClientRect();
          if (width > 0) setDims(d => ({ ...d, w: width }));
        }
      });
    }
    return () => obs.disconnect();
  }, []);

  if (!data.length) return null;

  const fmt$ = v => new Intl.NumberFormat("en-US", { style:"currency", currency:"USD", minimumFractionDigits:0, maximumFractionDigits:0 }).format(v);

  const fmtMonth = (m) => {
    const [y, mo] = m.split("-");
    const names = ["JAN","FEB","MAR","APR","MAY","JUN","JUL","AUG","SEP","OCT","NOV","DEC"];
    return names[parseInt(mo)-1] + " '" + y.slice(2);
  };

  const PAD = { t:20, r:20, b:40, l:20 };
  const W = dims.w, H = dims.h;
  const gW = W - PAD.l - PAD.r;
  const gH = H - PAD.t - PAD.b;

  const n = data.length;
  const groupW = gW / n;
  const barW = Math.max(2, Math.min(7, groupW * 0.12));
  const gap = barW * 0.4;

  const maxDollars = Math.max(...data.map(d => d.dollars), 1);
  const maxShares  = Math.max(...data.map(d => d.shares), 0.01);
  const avgPrices  = data.map(d => d.shares > 0 ? d.dollars / d.shares : 0);
  const maxAvgPrice = Math.max(...avgPrices, 1);

  const xCenter  = i => PAD.l + i * groupW + groupW / 2;
  const yDollars = v => PAD.t + gH - (v / maxDollars) * gH;
  const yShares  = v => PAD.t + gH - (v / maxShares) * gH;
  const yAvgP    = v => PAD.t + gH - (v / maxAvgPrice) * gH;

  return (
    <div style={{ background:"var(--bg2)", border:"1px solid var(--border)", borderRadius:10, padding:"16px 16px 8px", marginBottom:24 }}>
      {/* Header + legend */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
        <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:11, fontWeight:700, letterSpacing:"0.3em", color:"#888" }}>
          MONTHLY BREAKDOWN
        </div>
        <div style={{ display:"flex", gap:16 }}>
          {[{ color:C.orange, label:"USD INVESTED" }, { color:"#60a5fa", label:"SHARES" }, { color:"#a78bfa", label:"AVG PRICE" }].map(({color,label}) => (
            <div key={label} style={{ display:"flex", alignItems:"center", gap:6 }}>
              <div style={{ width:10, height:10, borderRadius:2, background:color }}/>
              <span style={{ fontFamily:"Montserrat,sans-serif", fontSize:9, fontWeight:700, color:"var(--text4)", letterSpacing:"0.1em" }}>{label}</span>
            </div>
          ))}
        </div>
      </div>

      <div ref={wrapRef} style={{ position:"relative" }}>
        <svg width="100%" height={H} viewBox={`0 0 ${W} ${H}`} style={{ display:"block", overflow:"visible" }}>
          {/* Baseline */}
          <line x1={PAD.l} y1={PAD.t+gH} x2={W-PAD.r} y2={PAD.t+gH} stroke="var(--border)" strokeWidth="1"/>

          {data.map((d, i) => {
            const cx      = xCenter(i);
            const isHov   = hovered === i;
            const avgP    = d.shares > 0 ? d.dollars / d.shares : 0;
            const hDol    = PAD.t + gH - yDollars(d.dollars);
            const hShr    = PAD.t + gH - yShares(d.shares);
            const hAvg    = PAD.t + gH - yAvgP(avgP);
            const yDol    = yDollars(d.dollars);
            const yShr    = yShares(d.shares);
            const yAvg    = yAvgP(avgP);
            const rx      = Math.min(barW / 2, 4);
            // 3 bars centered: left=USD, center=shares, right=avgPrice
            const totalW  = barW * 3 + gap * 2;
            const x0      = cx - totalW / 2;

            return (
              <g key={d.month}
                onMouseEnter={() => { setHovered(i); setTooltip({ x: cx, y: Math.min(yDol, yShr, yAvg), d, avgP }); }}
                onMouseLeave={() => { setHovered(null); setTooltip(null); }}
                style={{ cursor:"default" }}>
                {/* USD bar */}
                <rect x={x0} y={yDol} width={barW} height={hDol} rx={rx} ry={rx}
                  fill={isHov ? C.orange : `${C.orange}88`} style={{ transition:"fill 0.15s" }}/>
                {/* Shares bar */}
                <rect x={x0 + barW + gap} y={yShr} width={barW} height={hShr} rx={rx} ry={rx}
                  fill={isHov ? "#60a5fa" : "#60a5fa88"} style={{ transition:"fill 0.15s" }}/>
                {/* Avg price bar */}
                <rect x={x0 + barW*2 + gap*2} y={yAvg} width={barW} height={hAvg} rx={rx} ry={rx}
                  fill={isHov ? "#a78bfa" : "#a78bfa88"} style={{ transition:"fill 0.15s" }}/>
                {/* Month label */}
                <text x={cx} y={H - 6} textAnchor="middle"
                  fill={isHov ? C.orange : "var(--text5)"}
                  fontSize="8" fontFamily="Montserrat,sans-serif" fontWeight="700"
                  style={{ transition:"fill 0.15s" }}>
                  {fmtMonth(d.month).split(" ")[0]}
                </text>
              </g>
            );
          })}

          {/* Tooltip */}
          {tooltip && (() => {
            const tx = Math.min(Math.max(tooltip.x - 75, 4), W - 155);
            const ty = Math.max(tooltip.y - 96, 4);
            return (
              <g>
                <rect x={tx} y={ty} width={150} height={90} rx="6"
                  fill="var(--bg2)" stroke={C.orange} strokeWidth="1"/>
                <text x={tx+10} y={ty+14} fill="var(--text4)" fontSize="8"
                  fontFamily="Montserrat,sans-serif" fontWeight="700" letterSpacing="0.15em">
                  {fmtMonth(tooltip.d.month)}
                </text>
                <text x={tx+10} y={ty+32} fill="var(--text3)" fontSize="8" fontFamily="Montserrat,sans-serif" fontWeight="700">INVESTED</text>
                <text x={tx+140} y={ty+32} textAnchor="end" fill={C.orange} fontSize="11" fontFamily="Montserrat,sans-serif" fontWeight="900">{fmt$(tooltip.d.dollars)}</text>
                <text x={tx+10} y={ty+52} fill="var(--text3)" fontSize="8" fontFamily="Montserrat,sans-serif" fontWeight="700">SHARES</text>
                <text x={tx+140} y={ty+52} textAnchor="end" fill="#60a5fa" fontSize="11" fontFamily="Montserrat,sans-serif" fontWeight="900">{tooltip.d.shares.toFixed(4)}</text>
                <text x={tx+10} y={ty+72} fill="var(--text3)" fontSize="8" fontFamily="Montserrat,sans-serif" fontWeight="700">{t("avgPrice")}</text>
                <text x={tx+140} y={ty+72} textAnchor="end" fill="#a78bfa" fontSize="11" fontFamily="Montserrat,sans-serif" fontWeight="900">${tooltip.avgP.toFixed(2)}</text>
              </g>
            );
          })()}
        </svg>
      </div>
    </div>
  );
}

export default MonthlyBarsChart;
